import React, { useEffect, useState } from "react";
import * as ai from "../../services/aiAssistantService";
import { draftAcceptance, formatTokens, monthLabel } from "./aiHelpers";

const Tile = ({ label, value, sub }) => (
  <div className="col-6 col-lg-3">
    <div className="card shadow-sm rounded-4 h-100">
      <div className="card-body">
        <div className="text-muted small">{label}</div>
        <div className="fs-4 fw-semibold">{value}</div>
        {sub && <div className="text-muted small">{sub}</div>}
      </div>
    </div>
  </div>
);

// How much the assistant is being used and, in shadow mode, how often your team keeps its drafts.
const UsageTab = ({ settings, usage }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    ai.getUsage()
      .then((r) => !cancelled && setReport(r))
      .catch((e) => !cancelled && setError((e && e.message) || "Could not load usage"));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!report) return <div className="text-muted py-4">Loading…</div>;

  const acceptance = draftAcceptance(report.drafts_30d);
  const cap = settings.monthly_reply_cap;
  const tokens = usage.input_tokens + usage.output_tokens;

  return (
    <>
      <div className="row g-3 mb-3">
        <Tile label="Replies this month" value={usage.replies} sub={cap ? `of ${cap} included` : "no limit set"} />
        <Tile label="Conversations, last 30 days" value={report.conversations_30d} />
        <Tile label="Words processed this month" value={formatTokens(tokens)} sub="tokens" />
        <Tile
          label="Drafts your team kept"
          value={acceptance.total ? `${acceptance.used}%` : "-"}
          sub={acceptance.total ? `${acceptance.asWritten}% sent as written` : "shadow mode, last 30 days"}
        />
      </div>

      {acceptance.total > 0 && (
        <div className="card shadow-sm rounded-4 mb-3">
          <div className="card-body">
            <h6 className="fw-semibold">Shadow drafts, last 30 days</h6>
            <p className="small mb-1">
              Sent as written: <strong>{report.drafts_30d.sent}</strong> · Edited then sent: <strong>{report.drafts_30d.edited}</strong> · Discarded:{" "}
              <strong>{report.drafts_30d.discarded}</strong>
            </p>
            <p className="text-muted small mb-0">
              This is the best sign of whether the assistant is ready to go live: most drafts should be sent as written or with small edits.
            </p>
          </div>
        </div>
      )}

      <div className="table-responsive card shadow-sm rounded-4">
        <table className="table mb-0 align-middle">
          <thead>
            <tr className="small text-muted">
              <th>Month</th>
              <th>Replies</th>
              <th>Tokens in</th>
              <th>Tokens out</th>
            </tr>
          </thead>
          <tbody>
            {report.months.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted small py-4">
                  No usage yet.
                </td>
              </tr>
            )}
            {report.months.map((m) => (
              <tr key={m.ym}>
                <td>{monthLabel(m.ym)}</td>
                <td>{m.replies}</td>
                <td>{formatTokens(m.input_tokens)}</td>
                <td>{formatTokens(m.output_tokens)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UsageTab;
